import React, { useEffect, useRef, useState } from 'react';
import type { IconName } from '../icon';
import {
	PillRoot,
	PillMainBox,
	PillCheckbox,
	PillSparklesIcon,
	PillLabel,
	PillInput,
	PillActions,
	PillCloseButton,
	PillSendButton,
} from './AIChatPill.Styled';

export type AIChatPillProps = {
	onSend: (value: string) => void;
	placeholder?: string;
	isSending?: boolean;
	label?: string;
	/** Controlled expanded state. When provided, caller must update it in response to onExpandedChange. */
	isExpanded?: boolean;
	/** Called when the pill tries to expand (true) or collapse (false). */
	onExpandedChange?: (expanded: boolean) => void;
	/** When provided, shows a chat toggle button in the expanded pill actions. */
	onChatToggle?: () => void;
	/** Icon for the chat toggle button. Required when onChatToggle is set. */
	chatToggleIcon?: IconName;
	/** Whether the chat panel is currently open. Hides the close button when true. */
	isChatOpen?: boolean;
};

export function AIChatPill({
	onSend,
	placeholder = 'Describe what you\'d like to add or change…',
	isSending = false,
	label = 'Build with AI',
	isExpanded: isExpandedProp,
	onExpandedChange,
	onChatToggle,
	chatToggleIcon,
	isChatOpen = false,
}: AIChatPillProps): React.ReactElement {
	const [value, setValue] = useState('');
	const [localExpanded, setLocalExpanded] = useState(false);
	const checkboxRef = useRef<HTMLInputElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const isControlled = isExpandedProp !== undefined;
	const isExpanded = isControlled ? isExpandedProp : localExpanded;

	// Sync expanded state to checkbox DOM property — drives :has(:checked) CSS animations
	useEffect(() => {
		if (!checkboxRef.current) return;
		checkboxRef.current.checked = isExpanded;
		if (isExpanded) {
			setTimeout(() => inputRef.current?.focus(), 0);
		} else {
			setValue('');
			inputRef.current?.blur();
		}
	}, [isExpanded]);

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.checked) return;
		if (!isControlled) setLocalExpanded(true);
		onExpandedChange?.(true);
	};

	const handleCollapse = () => {
		if (isChatOpen) return;
		if (!isControlled) setLocalExpanded(false);
		onExpandedChange?.(false);
	};

	const handleSend = () => {
		const trimmed = value.trim();
		if (!trimmed || isSending) return;
		onSend(trimmed);
		setValue('');
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
			e.preventDefault();
			handleSend();
		}
		if (e.key === 'Escape') {
			handleCollapse();
		}
	};

	const canSend = value.trim().length > 0 && !isSending;

	const handleContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (!isExpanded && (e.key === 'Enter' || e.key === ' ')) {
			e.preventDefault();
			if (!isControlled) setLocalExpanded(true);
			onExpandedChange?.(true);
		}
	};

	return (
		<PillRoot>
			<PillMainBox
				tabIndex={isExpanded ? -1 : 0}
				onKeyDown={handleContainerKeyDown}
			>
				<PillCheckbox
					ref={checkboxRef}
					type="checkbox"
					aria-hidden="true"
					tabIndex={-1}
					onChange={handleCheckboxChange}
				/>
				<PillSparklesIcon icon="sparkles-regular" aria-hidden="true" />
				<PillLabel>{label}</PillLabel>
				<PillInput
					ref={inputRef}
					value={value}
					onChange={e => setValue(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					disabled={isSending}
					aria-label={placeholder}
					tabIndex={isExpanded ? 0 : -1}
				/>
				<PillActions>
					{!isChatOpen && (
						<PillCloseButton
							variant="ghost"
							size="small"
							disabled={isSending}
							aria-label="Close"
							onClick={handleCollapse}
							icon="x-slim"
							tabIndex={isExpanded ? 0 : -1}
						/>
					)}
					{onChatToggle && chatToggleIcon && (
						<PillCloseButton
							variant="ghost"
							size="small"
							aria-label={isChatOpen ? 'Close chat' : 'Open chat'}
							onClick={onChatToggle}
							icon={chatToggleIcon}
							tabIndex={isExpanded ? 0 : -1}
						/>
					)}
					<PillSendButton
						variant="ghost"
						size="small"
						aria-label="Send"
						onClick={handleSend}
						disabled={!canSend}
						icon="send"
						tabIndex={isExpanded ? 0 : -1}
					/>
				</PillActions>
			</PillMainBox>
		</PillRoot>
	);
}
